import React, { useState } from 'react';
import { TRANSPORT_SERVICE_OPTIONS } from '../../constants/transportServiceOptions';
import { BulkApplyConfirmModal } from './BulkApplyConfirmModal';
import {
  EMPTY_BULK_DRAFT,
  PURCHASE_ORDER_BULK_FIELD_DEFS,
  buildBulkConfirmPreview,
  buildBulkUpdatePayload,
  getBulkTargetSummaryLabel,
  resolveBulkTargetType,
  resolveEffectiveBulkApplyFields,
  validateBulkBeforeSubmit,
} from './shipmentPlanFieldUtils';

const fieldClassName =
  'w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900';

const BulkFieldInput = ({ def, value, onChange }) => {
  if (def.type === 'date') {
    return (
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`max-w-xs ${fieldClassName}`}
      />
    );
  }

  if (def.type === 'textarea') {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${fieldClassName} resize-y min-h-[60px]`}
      />
    );
  }

  if (def.type === 'text') {
    return (
      <input
        type="text"
        value={value}
        onChange={(e) => {
          const next = e.target.value;
          if (def.name === 'tracking_number' && next !== '' && !/^\d+$/.test(next)) {
            return;
          }
          onChange(next);
        }}
        placeholder={def.name === 'tracking_number' ? '数値のみ' : ''}
        className={fieldClassName}
      />
    );
  }

  if (def.type === 'transport_service') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={fieldClassName}>
        <option value="">選択してください</option>
        {TRANSPORT_SERVICE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (def.type === 'select') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={fieldClassName}>
        <option value="">選択してください</option>
        {(def.options || []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  return null;
};

export const BulkShipmentPlanForm = ({
  targetDetails,
  selectedCount,
  selectedDatacodes,
  containsSplitPlans,
  onApply,
  saving,
}) => {
  const [draft, setDraft] = useState({ ...EMPTY_BULK_DRAFT });
  const [touchedFields, setTouchedFields] = useState(new Set());
  const [formError, setFormError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState(null);
  const [confirmApplyFields, setConfirmApplyFields] = useState([]);
  const [confirmError, setConfirmError] = useState('');
  const targetType = resolveBulkTargetType(selectedCount);

  const markFieldTouched = (fieldName) => {
    setTouchedFields((prev) => {
      if (prev.has(fieldName)) return prev;
      const next = new Set(prev);
      next.add(fieldName);
      return next;
    });
  };

  const updateDraftField = (fieldName, value) => {
    markFieldTouched(fieldName);
    setDraft((prev) => ({ ...prev, [fieldName]: value }));
    if (formError) setFormError('');
  };

  const resetForm = () => {
    setDraft({ ...EMPTY_BULK_DRAFT });
    setTouchedFields(new Set());
    setFormError('');
  };

  const closeConfirmModal = () => {
    if (saving) return;
    setConfirmOpen(false);
    setConfirmPreview(null);
    setConfirmApplyFields([]);
    setConfirmError('');
  };

  const handleApplyClick = () => {
    const fields = resolveEffectiveBulkApplyFields(touchedFields, draft);
    const validationError = validateBulkBeforeSubmit(draft, fields, selectedCount);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    setConfirmApplyFields(fields);
    setConfirmPreview(
      buildBulkConfirmPreview({
        targetDetails,
        draft,
        applyFields: fields,
        containsSplitPlans,
      })
    );
    setConfirmError('');
    setConfirmOpen(true);
  };

  const handleConfirmApply = async () => {
    setConfirmError('');
    try {
      const payload = buildBulkUpdatePayload({
        draft,
        applyFields: confirmApplyFields,
        targetType,
        selectedDatacodes,
      });
      const result = await onApply(payload);
      if (result?.success) {
        closeConfirmModal();
        resetForm();
      } else {
        setConfirmError(result?.error || '一括更新に失敗しました');
      }
    } catch (error) {
      setConfirmError(error.message || '一括更新に失敗しました');
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="font-semibold text-lg mb-4 text-slate-900">一括設定</h3>

        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            適用対象: {getBulkTargetSummaryLabel(selectedCount)}
          </p>

          <div className="grid grid-cols-2 gap-4">
            {PURCHASE_ORDER_BULK_FIELD_DEFS.map((def) => (
              <div key={def.name} className={def.layout === 'full' ? 'col-span-2' : ''}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{def.label}</label>
                <BulkFieldInput
                  def={def}
                  value={draft[def.name]}
                  onChange={(value) => updateDraftField(def.name, value)}
                />
              </div>
            ))}
          </div>

          {formError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {formError}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleApplyClick}
              disabled={saving}
              className="px-4 py-2 bg-slate-700 text-white text-sm rounded-md hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              適用
            </button>
          </div>
        </div>
      </div>

      <BulkApplyConfirmModal
        isOpen={confirmOpen}
        onClose={closeConfirmModal}
        onConfirm={handleConfirmApply}
        preview={confirmPreview}
        saving={saving}
        errorMessage={confirmError}
      />
    </>
  );
};
